import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Icon, type IconName } from './Icon';

export interface TextTooltipProps {
  /** Glyph rendered before `label`. */
  icon: IconName;
  /** Visible trigger text — also the tooltip's accessible name, so no separate `aria-label` is needed. */
  label: ReactNode;
  /** Tooltip content. */
  content: ReactNode;
  /** Override the trigger row's default styling (size, color, wrapping). */
  className?: string;
  /** Override the tooltip box's default `max-w-72`. */
  contentClassName?: string;
}

/**
 * A hint trigger that pairs an icon with visible label text (e.g. "Adjust payment plan"), for
 * supplemental info that's too long or too formal for a bare `InfoDot`. Use `InfoDot` instead when
 * the trigger is icon-only.
 *
 * Controlled `open` state: Base UI's `TooltipTrigger` only wires up hover and keyboard focus, so a
 * touch device gets no way in at all — which matters since this ships inside a WKWebView on iOS.
 * `onClick` therefore always opens (never toggles) — Base UI's own `closeOnClick` already handles
 * closing the tooltip when its trigger is pressed again, so toggling here would race that. Same
 * approach as `InfoDot`.
 */
export function TextTooltip({ icon, label, content, className, contentClassName }: TextTooltipProps) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger
          onClick={() => setOpen(true)}
          className={cn(
            'inline-flex cursor-help items-center gap-1.5 rounded-sm text-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            className,
          )}
        >
          <Icon name={icon} size="sm" />
          {label}
        </TooltipTrigger>
        <TooltipContent className={cn('max-w-72 items-start whitespace-pre-line text-left', contentClassName)}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
